/**
 * Billing API: backend-only. No Stripe secrets; all flows use backend-created sessions.
 * Do not treat return URL as proof of payment; use backend billing summary after return.
 */
import api from './api';
import {
  BILLING_SUMMARY,
  BILLING_VALIDATE_PROMO,
  BILLING_UPGRADE_PREVIEW,
  BILLING_UPGRADE_START,
  BILLING_CHECKOUT_SUBSCRIPTION,
  BILLING_CHECKOUT_ADDON,
  BILLING_PORTAL,
  BILLING_CHANGE_PLAN,
  BILLING_CANCEL,
  BILLING_REACTIVATE,
} from '../config/Config';

/**
 * @returns {Promise<{ subscription: object|null, addons: Array }>}
 */
export const getBillingSummary = async () => {
  const { data } = await api.get(BILLING_SUMMARY);
  return data;
};

/**
 * Validate promo code (optional plan_code for subscription). Returns { valid, message, discount_type, discount_amount }.
 * @param {string} code
 * @param {string} [planCode]
 * @returns {Promise<{ valid: boolean, message: string, discount_type?: string, discount_amount?: string }>}
 */
export const validatePromo = async (code, planCode = null) => {
  const { data } = await api.post(BILLING_VALIDATE_PROMO, {
    code: (code || '').trim(),
    ...(planCode ? { plan_code: planCode } : {}),
  });
  return data;
};

/**
 * Prorated upgrade preview (Stripe = source of truth). For current plan + plan_code upgrade.
 * @param {string} planCode
 * @returns {Promise<{ current_plan_name, new_plan_name, charge_now_cents, charge_now_formatted, credit_formatted, next_recurring_formatted, next_billing_date, summary }>}
 */
export const getUpgradePreview = async (planCode) => {
  const { data } = await api.post(BILLING_UPGRADE_PREVIEW, { plan_code: planCode });
  return data;
};

/**
 * Start prorated upgrade. Returns { applied } or { requires_action, client_secret }.
 * @param {string} planCode
 * @param {string} [changeReason]
 * @param {string} [promoCode]
 * @returns {Promise<object>}
 */
export const startUpgrade = async (planCode, changeReason = 'User initiated upgrade', promoCode = null) => {
  const body = { plan_code: planCode, change_reason: changeReason };
  if (promoCode && String(promoCode).trim()) body.promo_code = String(promoCode).trim();
  const { data } = await api.post(BILLING_UPGRADE_START, body);
  return data;
};

/**
 * Create Stripe Checkout session for subscription. Redirect user to returned URL.
 * @param {string} planCode
 * @param {string} [successPath='/profile']
 * @param {string} [cancelPath='/profile']
 * @param {string} [promoCode]
 * @returns {Promise<{ session_id: string, url: string|null }>}
 */
export const createCheckoutSubscription = async (
  planCode,
  successPath = '/profile',
  cancelPath = '/profile',
  promoCode = null
) => {
  const body = {
    plan_code: planCode,
    success_path: successPath,
    cancel_path: cancelPath,
  };
  if (promoCode && String(promoCode).trim()) body.promo_code = String(promoCode).trim();
  const { data } = await api.post(BILLING_CHECKOUT_SUBSCRIPTION, body);
  return data;
};

/**
 * Create Stripe Checkout session for one-time add-on. Cart: [{ addon_id, quantity }].
 * If cart omitted, backend may use session cart.
 * @param {Array<{ addon_id: number, quantity: number }>} [cart]
 * @param {string} [successPath='/profile']
 * @param {string} [cancelPath='/profile']
 * @param {string} [promoCode]
 * @returns {Promise<{ session_id: string, url: string|null }>}
 */
export const createCheckoutAddon = async (
  cart = null,
  successPath = '/profile',
  cancelPath = '/profile',
  promoCode = null
) => {
  const body = { success_path: successPath, cancel_path: cancelPath };
  if (cart && cart.length) body.cart = cart;
  if (promoCode && String(promoCode).trim()) body.promo_code = String(promoCode).trim();
  const { data } = await api.post(BILLING_CHECKOUT_ADDON, body);
  return data;
};

/**
 * Create Stripe Customer Portal session. Redirect user to returned URL.
 * @param {string} [returnPath='/profile']
 * @returns {Promise<{ url: string }>}
 */
export const createPortalSession = async (returnPath = '/profile') => {
  const { data } = await api.post(BILLING_PORTAL, { return_path: returnPath });
  return data;
};

/**
 * Change plan (upgrade/downgrade/free). Backend applies scheduling rules.
 * @param {string} planCode
 * @param {string} [changeReason='User initiated change']
 * @returns {Promise<object>}
 */
export const changePlan = async (planCode, changeReason = 'User initiated change') => {
  const { data } = await api.post(BILLING_CHANGE_PLAN, {
    plan_code: planCode,
    change_reason: changeReason,
  });
  return data;
};

/**
 * Cancel subscription at period end.
 * @param {number} [subscriptionId] - optional; backend uses active subscription if omitted
 * @returns {Promise<object>}
 */
export const cancelSubscription = async (subscriptionId = null) => {
  const body = subscriptionId != null ? { subscription_id: subscriptionId } : {};
  const { data } = await api.post(BILLING_CANCEL, body);
  return data;
};

/**
 * Reactivate a canceled subscription.
 * @param {number} subscriptionId
 * @returns {Promise<object>}
 */
export const reactivateSubscription = async (subscriptionId) => {
  const { data } = await api.post(BILLING_REACTIVATE, {
    subscription_id: subscriptionId,
  });
  return data;
};
